import { isNil } from 'lodash'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { BaseInputWrapperProps } from '~/@/components/inputs/base/types'
import { DataType, TableColumn } from '~/common/generated/apollo-operations'
import { RecordCellEditOrigin } from '../../../table-cells/useEditableTableCellOriginProvider'
import { CellValue } from '../../add-record/AddRecordDialog'
import { AddRecordFormCell, RecordDetailTableCell, RecordDetailTableRow } from '../../types'
import { EnumArrayCellDetails } from './EnumArrayCellDetails'
import { FileArrayCellDetails } from './FileArrayCellDetails'
import { LookupArrayCellDetails } from './LookupArrayCellDetails'
import { UserArrayCellDetails } from './UserArrayCellDetails'

interface ArrayCellDetailsProps {
  fieldName: string
  cell: RecordDetailTableCell | AddRecordFormCell
  enableAutofocus: boolean
  value: CellValue
  addDocumentId?: (documentId: string) => void
  removeDocumentId?: (documentId: string) => void
  showDirtyIndicator: boolean
  onCancel?: () => void
  error?: boolean
  columnWidth?: number
  parentRow?: Omit<RecordDetailTableRow, 'table' | 'rootWorkspace'>
  origin?: RecordCellEditOrigin | null
  columns: Pick<TableColumn, 'id' | 'type' | 'innerType' | 'indexType'>[]
  selectedLookupRowIds: string[]
  row?: Pick<RecordDetailTableRow, 'cells'>
  inputWrapperProps?: BaseInputWrapperProps
}

/**
 * Renders the record details for a cell with column of type ARRAY
 */
export function ArrayCellDetails({
  fieldName,
  cell,
  enableAutofocus,
  value,
  addDocumentId,
  removeDocumentId,
  showDirtyIndicator,
  onCancel,
  error,
  inputWrapperProps,
  columnWidth,
  parentRow,
  columns,
  origin,
  selectedLookupRowIds,
  row,
}: ArrayCellDetailsProps) {
  if (isNil(cell.column.innerType)) {
    throw new Error(`Missing innerType for column ${cell.column.id}`)
  }

  switch (cell.column.innerType) {
    case DataType.ENUM:
      if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
        return (
          <EnumArrayCellDetails
            cell={cell}
            columnWidth={columnWidth}
            enableAutofocus={enableAutofocus}
            error={error}
            fieldName={fieldName}
            inputWrapperProps={inputWrapperProps}
            origin={origin}
            // At this point we already know that value is a string[] but TS thinks it may be a
            // lookup array so we need to cast it
            value={value as string[]}
          />
        )
      }
      return null
    case DataType.FILE_URL:
      return (
        <InputWrapper {...inputWrapperProps}>
          <FileArrayCellDetails
            addDocumentId={addDocumentId}
            cell={cell}
            removeDocumentId={removeDocumentId}
          />
        </InputWrapper>
      )
    case DataType.USER:
      return (
        <UserArrayCellDetails
          cell={cell}
          fieldName={fieldName}
          inputWrapperProps={inputWrapperProps}
          origin={origin}
          value={value}
        />
      )
    case DataType.LOOKUP:
      return (
        <InputWrapper {...inputWrapperProps}>
          <LookupArrayCellDetails
            cell={cell}
            columnWidth={columnWidth}
            columns={columns}
            onCancel={onCancel}
            parentRow={parentRow}
            row={row}
            selectedLookupRowIds={selectedLookupRowIds}
            showDirtyIndicator={showDirtyIndicator}
          />
        </InputWrapper>
      )
    case DataType.ID:
    case DataType.STRING:
    case DataType.NUMBER:
    case DataType.BOOLEAN:
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.FORMULA:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
    case DataType.ARRAY:
      throw new Error(`Unsupported array type: ${cell.column.innerType}`)
  }
}
